'use client';
import classNames from 'classnames';
import './index.scss';
import { EcoSelectorItemType, EcoSelectorType } from './typs';
import React, { useEffect, useState, useRef } from 'react';

/**
 *  @params name 下拉选择框，带搜索/清除功能
 *  @params description 用于选择下拉选项，支持搜索/清除功能
 *  @params author kalkin
 *  @params date 2025-04-01
 *  @params version 1.0.0
 *
 *  @params name 下拉选择器，点击区域外关闭
 *  @params description 点击区域外关闭下拉选择器
 *  @params author kalkin
 *  @params date 2025-06-06
 *  @params version 1.0.1
 */
const EcoSeletor: React.FC<EcoSelectorType> = ({
    className = '',
    theme = 'white',
    defaultValue,
    placeholder = '',
    disabled = false,
    size = 'middle',
    showSearch = false,
    allowClear = false,
    onChange,
    onSearch,
    onClick,
    items,
}) => {
    const prefixCls = 'eco-selector';
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-size-${size}`]: size,
        [`${prefixCls}-disabled`]: disabled,
        [`${prefixCls}-search`]: showSearch,
        [`${prefixCls}-clear`]: allowClear,
    });

    const selectorRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(
        ['number', 'string'].includes(typeof defaultValue)
            ? items?.find((item: EcoSelectorItemType) => item.label === defaultValue || item.value === defaultValue) || {
                  label: defaultValue,
                  value: defaultValue,
              }
            : ({} as EcoSelectorItemType),
    );
    const [isFocus, setIsFocus] = useState(false);
    const [options, setOptions] = useState<EcoSelectorItemType[]>(items);
    const [search, setSearch] = useState('');

    const handleClick = () => {
        if (disabled) return;
        onClick?.();
        setIsOpen(!isOpen);
    };

    const handleSelect = (item: EcoSelectorItemType) => {
        if (disabled || JSON.stringify(item) === JSON.stringify(selected)) {
            return;
        }
        setSelected(item);
        if (showSearch) {
            setSearch('');
            setOptions(items);
        }
        setIsOpen(false);
        onChange && onChange(item);
    };

    const handleFocus = () => {
        setIsFocus(true);
        setIsOpen(true);
    };

    const handleBlur = () => {
        setIsFocus(false);
        if (showSearch) {
            setSearch('');
            setOptions(items);
        }
        setIsOpen(false);
    };

    const handleSearch = (e: any) => {
        if (disabled) {
            return;
        }
        const v = e.target.value;
        const newOptions = items.filter((item) => item.label.includes(v));
        setOptions(JSON.parse(JSON.stringify(newOptions || [])));
        setSearch(v);
        onSearch && onSearch(v);
    };

    const handleClear = () => {
        if (disabled) {
            return;
        }
        setSelected({ label: '', value: '' });
        setSearch('');
        setOptions(items);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const handleClickOutside = (e: MouseEvent) => {
                if (e.target instanceof HTMLElement && selectorRef.current && !selectorRef.current.contains(e.target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => document.removeEventListener('mousedown', handleClickOutside, true);
        }
    }, [isOpen, prefixCls]);

    return (
        <div className={`${compCls}`} ref={selectorRef} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className={`${prefixCls}-selector`} onClick={handleClick}>
                {showSearch && !disabled && (
                    <div className={`${prefixCls}-search-box`}>
                        <input
                            className={`${prefixCls}-search-input`}
                            value={search}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            onChange={handleSearch}
                        />
                    </div>
                )}
                {!search && (
                    <p className={`${prefixCls}-content`}>
                        <input className={`${prefixCls}-value`} value={selected.label} onBlur={() => setIsOpen(false)} />
                    </p>
                )}
                {placeholder && !selected.value && !isFocus && !search && <span className={`${prefixCls}-placeholder`}>{placeholder}</span>}
            </div>
            <span className={`${prefixCls}-suffix-icon ${prefixCls}-icon`}>
                {isOpen && showSearch && (
                    <svg className={`${prefixCls}-icon-svg`} xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="6" stroke="currentColor" stroke-width="1.6"></circle>
                        <path
                            d="M19.3841 20.5154C19.6965 20.8279 20.203 20.8279 20.5154 20.5154C20.8279 20.203 20.8279 19.6965 20.5154 19.3841L19.3841 20.5154ZM14.4343 15.5657L19.3841 20.5154L20.5154 19.3841L15.5657 14.4343L14.4343 15.5657Z"
                            fill="currentColor"
                        ></path>
                    </svg>
                )}
                {(!showSearch || !isOpen) && (
                    <svg
                        className={`${prefixCls}-icon-svg ${prefixCls}-icon-svg-${isOpen ? 'open' : 'close'}`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="1em"
                        height="1em"
                        viewBox="0 0 13 13"
                        fill="none"
                    >
                        <path
                            d="M10.5625 4.60417L6.22917 8.9375L1.89583 4.60416"
                            stroke="currentColor"
                            stroke-width="1.1375"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></path>
                    </svg>
                )}
            </span>
            {!disabled && allowClear && selected.value && (
                <svg
                    className={`${prefixCls}-icon-clear`}
                    fill-rule="evenodd"
                    viewBox="64 64 896 896"
                    width="1em"
                    height="1em"
                    fill="currentColor"
                    aria-hidden="true"
                    onClick={handleClear}
                >
                    <path d="M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm0 76c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372zm128.01 198.83c.03 0 .05.01.09.06l45.02 45.01a.2.2 0 01.05.09.12.12 0 010 .07c0 .02-.01.04-.05.08L557.25 512l127.87 127.86a.27.27 0 01.05.06v.02a.12.12 0 010 .07c0 .03-.01.05-.05.09l-45.02 45.02a.2.2 0 01-.09.05.12.12 0 01-.07 0c-.02 0-.04-.01-.08-.05L512 557.25 384.14 685.12c-.04.04-.06.05-.08.05a.12.12 0 01-.07 0c-.03 0-.05-.01-.09-.05l-45.02-45.02a.2.2 0 01-.05-.09.12.12 0 010-.07c0-.02.01-.04.06-.08L466.75 512 338.88 384.14a.27.27 0 01-.05-.06l-.01-.02a.12.12 0 010-.07c0-.03.01-.05.05-.09l45.02-45.02a.2.2 0 01.09-.05.12.12 0 01.07 0c.02 0 .04.01.08.06L512 466.75l127.86-127.86c.04-.05.06-.06.08-.06a.12.12 0 01.07 0z"></path>
                </svg>
            )}
            {isOpen && !disabled && (
                <ul
                    className={`${prefixCls}-options${options?.length > 8 ? ` ${prefixCls}-options-hidden` : ''}${
                        options?.length <= 0 ? ` ${prefixCls}-options-no-data` : ''
                    }`}
                >
                    {options.map((item: EcoSelectorItemType) => (
                        <li
                            className={`${prefixCls}-option${JSON.stringify(item) === JSON.stringify(selected) ? ` ${prefixCls}-option-selected` : ''}`}
                            key={item.value}
                            // 这里不能使用onClick => onClick会后于onBlur事件触发，搜索时导致isOpen状态已关闭，无法触发onClick事件
                            onMouseDown={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleSelect(item);
                            }}
                            title={item.label}
                        >
                            {item.label}
                        </li>
                    ))}
                    {options?.length <= 0 && (
                        <li className={`${prefixCls}-option`}>
                            <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                <g transform="translate(0 1)" fill="none" fill-rule="evenodd">
                                    <ellipse fill="#F5F5F5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                    <g fill-rule="nonzero" stroke="#d9d9d9">
                                        <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                        <path
                                            d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                                            fill="#FAFAFA"
                                        ></path>
                                    </g>
                                </g>
                            </svg>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default EcoSeletor;
